import {
  UI_CAPTION,
  UI_BODY_SM,
  UI_BODY_SEMIBOLD,
  LoaderCircleV2,
  TabBarV2,
  TooltipV2,
  colors2,
  icons,
  UI_CAPTION_BOLD,
  UI_BODY_BOLD_SM_DF,
  UI_BODY_BOLD_SM,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_TITLE,
  UI_BODY_BOLD,
} from "oolib";
import { Tag } from "oolib/dist/v2/components/Tags";
import { Accordion } from "oolib/dist/v2/components/Accordion";
import React, { useState } from "react";
import { ScoreBarChart } from "../../components/dataviz/ScoreBarChart";
import { useGetChunkInsightChunks } from "./hooks/useGetChunkInsightChunks";
import { CardAnnoExplorer } from "../../components/analysisModules/AnnotationExplorer/comps/CardAnnoExplorer";
import { getProperTitleFromContentTypeId } from "../../components/analysisModules/AnnotationExplorer/utils/getProperTitleFromContentTypeId";
import { DIMENSIONS, DIMENSIONS_V2, DIMENSIONS_EVIDENCE_SYNTHESIS, getScoreColor, getPillConfig, ATTRIBUTE_LABELS, INSIGHT_COLORS } from "./insightConstants";
import { BarChart } from "../../components/dataviz/BarChart";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";

const { Info } = icons;

// ── Slice × Attribute BarChart data builder ─────────────────────

/**
 * Transform finding metrics into BarChart-compatible data.
 * Returns null if insufficient data.
 */
const buildSliceChartData = (insight) => {
  const distributions = insight.metrics?.sliceSecondaryDistributions;
  const sliceCounts = insight.metrics?.sliceCounts;
  if (!distributions || !sliceCounts) return null;

  const sliceEntries = Object.entries(sliceCounts).sort((a, b) => b[1] - a[1]);
  if (!sliceEntries.length) return null;

  // Collect all unique secondary attribute values across all slices
  const allSecondaryValues = new Set();
  for (const dist of Object.values(distributions)) {
    for (const key of Object.keys(dist)) allSecondaryValues.add(key);
  }
  const secondaryKeys = [...allSecondaryValues].sort();
  if (!secondaryKeys.length) return null;

  // For "general" patterns with a single "overall" slice, show a simple bar per attribute value
  if (sliceEntries.length === 1) {
    const [, dist] = [sliceEntries[0][0], distributions[sliceEntries[0][0]] || {}];
    const data = secondaryKeys.map((key) => ({
      display: key,
      count: dist[key] || 0,
    }));
    return { data, isBreakdown: false };
  }

  // Breakdown: one group per slice, children = secondary attribute values
  const data = sliceEntries.map(([sliceName]) => ({
    display: sliceName,
    count: sliceCounts[sliceName] || 0,
    children: secondaryKeys.map((key) => ({
      display: key,
      count: distributions[sliceName]?.[key] || 0,
    })),
  }));

  return { data, isBreakdown: true, secondaryKeys };
};

// Safely extract display string from attribute values which can be:
// string, [string], [{display, tagId}], or {display, tagId}
const getAttrDisplay = (attr) => {
  if (!attr) return null;
  if (typeof attr === "string") return attr;
  if (Array.isArray(attr)) {
    const first = attr[0];
    if (!first) return null;
    return typeof first === "string" ? first : first.display || null;
  }
  return attr.display || null;
};

// Same as SM_Dashboard/index.js — builds tags from chunk enrichments
const buildChunkTags = (chunk) => {
  const tags = {};
  if (chunk.sentiment) {
    const parts = [chunk.sentiment.emotion, chunk.sentiment.polarity].filter(Boolean);
    if (parts.length) {
      tags.sentiment = {
        collectionId: "Sentiment",
        data: parts.map((v) => ({ display: v.charAt(0).toUpperCase() + v.slice(1), _id: v })),
      };
    }
  }
  if (chunk.topics?.length) {
    tags.topics = {
      collectionId: "AI Topics",
      data: chunk.topics.map((t) => ({
        display: t.parentTopic ? `${t.name} (${t.parentTopic})` : t.name,
        _id: t.name,
      })),
    };
  }
  if (chunk.entities?.length) {
    tags.entities = {
      collectionId: "Named Entities",
      data: chunk.entities.map((e) => ({
        display: `${e.name} [${e.type}]`,
        _id: e.name,
      })),
    };
  }
  return tags;
};

const chunkToAnnoDoc = (chunk) => ({
  ...chunk,
  main: { ...chunk.main, title: chunk.main?.title || "Untitled" },
  annotations: {
    ...chunk.annotations,
    annoKey: chunk._id,
    tags: buildChunkTags(chunk),
  },
  fills: [],
});

const CARD_CONFIGS = { annoTagsTitle: "NLP Tags", metaBlock: [] };

// ── Dimension row with pill + oolib Accordion ───────────────────

const DimensionRow = ({ dim, score, detail }) => {
  const score10 = Math.round(score * 10);
  const pill = getPillConfig(score10);

  const titleContent = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, paddingRight: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <UI_BODY_SEMIBOLD_SM_DF >{dim.label}</UI_BODY_SEMIBOLD_SM_DF>
        <TooltipV2
          primaryContent={{
            layout: "custom",
            render: (
              <div style={{ maxWidth: 260, padding: "6px 8px" }}>
                <UI_CAPTION_BOLD>{dim.formula}</UI_CAPTION_BOLD>
                <UI_CAPTION color={colors2.white} style={{ marginTop: 4, fontSize: 11, lineHeight: 1.5 }}>
                  {dim.explanation}
                </UI_CAPTION>
              </div>
            ),
          }}
          trigger="hover"
          placement="bottom"
        >
          <Info size={14} color={colors2.grey30} style={{ cursor: "pointer" }} />
        </TooltipV2>
      </div>
      <Tag
        display={pill.label}
        fill="solid"
        style={{ background: pill.color, color: colors2.white, fontWeight: 600 }}
      />
    </div>
  );

  return (
    <Accordion title={titleContent} defaultExpanded={false} actionsConfig={null}>
      <div style={{ padding: "0 1.6rem 1rem" }}>
        <UI_CAPTION color={colors2.grey50} style={{ fontSize: 12, lineHeight: 1.5 }}>
          {dim.formatDetail(detail)}
        </UI_CAPTION>
      </div>
    </Accordion>
  );
};

// ── Tab content components ───────────────────────────────────────

// FindingsTab commented out — chunk cards now live in Evidences.
// Keeping the component around in case we need to reintroduce it.
// const FindingsTab = ({ chunkIds }) => {
//   const [chunks, setChunks] = useState(null);
//   const [loading, setLoading] = useState(true);
//
//   useEffect(() => {
//     if (!chunkIds?.length) {
//       setChunks([]);
//       setLoading(false);
//       return;
//     }
//     axios
//       .get("/api/playground/chunkInsights/chunks", {
//         params: { ids: chunkIds.join(",") },
//       })
//       .then((res) => setChunks(res.data.chunks || []))
//       .catch(() => setChunks([]))
//       .finally(() => setLoading(false));
//   }, [chunkIds?.join(",")]);
//
//   if (loading) return <div style={{ padding: "1rem", textAlign: "center" }}><LoaderCircleV2 /></div>;
//   if (!chunks?.length) return <UI_CAPTION color={colors2.grey40}>No findings available</UI_CAPTION>;
//
//   return (
//     <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
//       {chunks.map((c, i) => (
//         <CardAnnoExplorer
//           key={c._id || i}
//           singleAnno={chunkToAnnoDoc(c)}
//           cardConfigs={CARD_CONFIGS}
//         />
//       ))}
//     </div>
//   );
// };

const EvidencesTab = ({ chunkIds }) => {
  const { data: chunks, isLoading } = useGetChunkInsightChunks({ chunkIds });

  if (!chunkIds?.length) return <UI_CAPTION color={colors2.grey40}>No evidence available</UI_CAPTION>;
  if (isLoading) return <div style={{ padding: "1rem", textAlign: "center" }}><LoaderCircleV2 /></div>;
  if (!chunks?.length) return <UI_CAPTION color={colors2.grey40}>No evidence available</UI_CAPTION>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {chunks.map((c, i) => (
        <CardAnnoExplorer
          key={c._id || i}
          singleAnno={chunkToAnnoDoc(c)}
          cardConfigs={CARD_CONFIGS}
        />
      ))}
    </div>
  );
};

const SourcesTab = ({ contentTypes }) => {
  if (!contentTypes?.length) {
    return <UI_CAPTION color={colors2.grey40}>No source content types available</UI_CAPTION>;
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {contentTypes.map((type) => (
        <Tag key={type} display={getProperTitleFromContentTypeId(type)} fill="outline" size="XS" />
      ))}
    </div>
  );
};

// ── V2 Evidence Components ───────────────────────────────────────
          // <SentimentTopicsWidget polarity="positive" title="Top Positive Topics" color="#CBEDD6" useAnnotations={hasNewTopicModelling} />
          // <SentimentTopicsWidget polarity="negative" title="Top Negative Topics" color="#F5DCE6
const SENTIMENT_ACCENT_COLORS = {
  negative: "#EAB5CA",
  positive: "#8CDEA7",
  // positive: "#57BE45",
  // negative: "#FF6A6A",
  neutral: colors2.yellowStroke,
  mixed: INSIGHT_COLORS.pillMid,
};

const AttributeBadges = ({ attributes }) => {
  if (!attributes || !Object.keys(attributes).length) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
      {Object.entries(attributes).map(([key, values]) => {
        if (!Array.isArray(values) || !values.length) return null;
        const label = ATTRIBUTE_LABELS[key] || key;
        return values.map((v, i) => (
          <Tag
            key={`${key}-${i}`}
            display={`${label}: ${v.display || v}`}
            fill="outline"
            size="XS"
            style={{ fontSize: 10, opacity: 0.85 }}
          />
        ));
      })}
    </div>
  );
};

const SegmentBadges = ({ segments }) => {
  if (!segments || !Object.keys(segments).length) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {Object.entries(segments).map(([fieldPath, seg]) => {
        if (!seg?.label || seg.label === "[object Object]") return null;
        const fieldName = fieldPath.split("_").pop() || fieldPath;
        return (
          <Tag
            key={fieldPath}
            display={`${seg.label}`}
            fill="outline"
            size="XS"
            style={{ fontSize: 10 }}
          />
        );
      })}
    </div>
  );
};

const V2EvidenceCard = ({ evidence }) => {
  const sentiment = evidence.attributes?.sentiment_polarity?.[0]?.display?.toLowerCase() || "neutral";
  const accentColor = SENTIMENT_ACCENT_COLORS[sentiment] || SENTIMENT_ACCENT_COLORS.neutral;

  return (
    <div
      style={{
        border: `1px solid ${colors2.grey10}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex" }}>
        <div style={{ width: 4, background: accentColor, flexShrink: 0 }} />
        <div style={{ flex: 1, padding: "10px 12px" }}>
          {/* Quote */}
          <UI_CAPTION style={{ lineHeight: 1.5, fontStyle: "italic", color: colors2.grey70 }}>
            "{evidence.text}"
          </UI_CAPTION>

          {/* Document title + field source */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            {evidence.documentTitle && (
              <UI_CAPTION style={{ fontWeight: 600, fontSize: 11, color: colors2.grey50 }}>
                {evidence.documentTitle}
              </UI_CAPTION>
            )}
            {evidence.valuePath && (
              <Tag display={`from: ${evidence.valuePath.split(".").pop()}`} fill="outline" size="XS" style={{ fontSize: 9 }} />
            )}
          </div>

          {/* Thematic tags */}
          {evidence.thematicTags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {evidence.thematicTags.map((t, i) => (
                <Tag key={i} display={t.display} fill="solid" size="XS" style={{ fontSize: 10 }} />
              ))}
            </div>
          )}

          {/* All 11 attribute dimensions */}
          <AttributeBadges attributes={evidence.attributes} />

          {/* Document segment badges */}
          <SegmentBadges segments={evidence.documentSegments} />
        </div>
      </div>
    </div>
  );
};

// Transform V2 evidence item → singleAnno shape for CardAnnoExplorer
const v2EvidenceToAnnoDoc = (evidence, { fallbackContentType } = {}) => {
  const sentiment = getAttrDisplay(evidence.attributes?.sentiment_polarity)?.toLowerCase();
  const sentimentColor = SENTIMENT_ACCENT_COLORS[sentiment] || SENTIMENT_ACCENT_COLORS.neutral;

  // Build tags object: thematic tags + attribute tags grouped by type
  const tags = {};

  // Thematic tags
  if (evidence.thematicTags?.length) {
    tags.thematicTags = {
      collectionId: "Thematic Tags",
      data: evidence.thematicTags.map((t) => ({
        display: t.display || t.tagId,
        _id: t.tagId || t.display,
      })),
    };
  }

  // Attribute tags — group each attribute type as a separate tag section
  if (evidence.attributes) {
    for (const [key, values] of Object.entries(evidence.attributes)) {
      if (!Array.isArray(values) || !values.length) continue;
      const label = ATTRIBUTE_LABELS[key] || key;
      tags[key] = {
        collectionId: label,
        data: values.map((v) => ({
          display: v.display || v,
          _id: v.tagId || v.display || v,
        })),
      };
    }
  }

  return {
    _id: evidence.annotationId || evidence.id || Math.random().toString(),
    meta: {
      contentType: evidence.contentType || fallbackContentType || "",
      documentId: evidence.documentId || "",
      valuePath: evidence.valuePath || "",
    },
    main: { title: evidence.documentTitle || "Untitled" },
    annotations: {
      annoKey: evidence.annotationId || evidence.id || "",
      fragment: { blocks: [{ text: evidence.text || "" }] },
      tags,
    },
    fills: [sentimentColor],
  };
};

const V2_EVIDENCE_CARD_CONFIGS = { metaBlock: [] };

const V2EvidencesTab = ({ evidence, contentTypes }) => {
  if (!evidence?.length) {
    return <UI_CAPTION color={colors2.grey40}>No evidence available</UI_CAPTION>;
  }
  const fallbackContentType = contentTypes?.length === 1 ? contentTypes[0] : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {evidence.map((e, i) => (
        <CardAnnoExplorer
          key={e.id || e.annotationId || i}
          singleAnno={v2EvidenceToAnnoDoc(e, { fallbackContentType })}
          cardConfigs={V2_EVIDENCE_CARD_CONFIGS}
        />
      ))}
    </div>
  );
};

const LensTab = ({ insight }) => {
  const isEvidenceSynthesis = insight.patternType === "evidence_synthesis" || insight.patternType === "edge_behavior" || !!insight.synthesisContext;

  // ── Evidence Synthesis view ────────────────────────────────
  if (isEvidenceSynthesis) {
    const ctx = insight.synthesisContext || {};
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Method card */}
        <div style={{ background: colors2.grey2, borderRadius: 10, padding: "14px 16px" }}>
          <UI_CAPTION style={{ color: colors2.grey40, fontSize: 11, marginBottom: 4 }}>Analysis Method</UI_CAPTION>
          <UI_BODY_SEMIBOLD style={{ fontSize: 13, marginBottom: 6 }}>Evidence Synthesis (LLM-driven)</UI_BODY_SEMIBOLD>
          <UI_CAPTION style={{ color: colors2.grey50, fontSize: 11, lineHeight: 1.5 }}>
            Quotes clustered by sub-theme, packaged with attributes (sentiment, intent, discourse role, certainty), then synthesized by LLM to extract behavioral mechanisms.
          </UI_CAPTION>
        </div>

        {/* Mechanism */}
        {ctx.mechanism && (
          <div style={{ borderRadius: 10, padding: "12px 16px", background: "#F0FDF4", borderLeft: `4px solid ${INSIGHT_COLORS.scoreHigh}` }}>
            <UI_CAPTION style={{ fontWeight: 700, color: INSIGHT_COLORS.scoreHigh, marginBottom: 4 }}>
              Behavioral Mechanism
            </UI_CAPTION>
            <UI_CAPTION style={{ color: colors2.grey70, lineHeight: 1.5 }}>
              {ctx.mechanism}
            </UI_CAPTION>
          </div>
        )}

        {/* Synthesis info */}
        <div>
          <UI_BODY_SEMIBOLD style={{ marginBottom: 8, fontSize: 13 }}>Synthesis Context</UI_BODY_SEMIBOLD>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {ctx.subTheme && (
              <UI_CAPTION style={{ color: colors2.grey50, fontSize: 12 }}>
                Sub-theme: <span style={{ color: colors2.grey70, fontWeight: 500 }}>{ctx.subTheme}</span>
                {ctx.theme && <span style={{ color: colors2.grey40 }}> ({ctx.theme})</span>}
              </UI_CAPTION>
            )}
            <UI_CAPTION style={{ color: colors2.grey50, fontSize: 12 }}>
              Evidence: <span style={{ color: colors2.grey70, fontWeight: 500 }}>{ctx.quotesUsed || 0} quotes</span> from <span style={{ color: colors2.grey70, fontWeight: 500 }}>{ctx.documentsUsed || 0} documents</span>
            </UI_CAPTION>
            <UI_CAPTION style={{ color: colors2.grey50, fontSize: 12 }}>
              Strength: <span style={{ color: colors2.grey70, fontWeight: 500 }}>{ctx.evidenceStrengthLabel || "moderate"}</span>
            </UI_CAPTION>
          </div>
        </div>

        {/* Tags covered (trimmed) */}
        {ctx.tagsCovered?.length > 0 && (
          <div>
            <UI_CAPTION style={{ color: colors2.grey40, fontSize: 11, marginBottom: 6 }}>Tags in this cluster</UI_CAPTION>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {ctx.tagsCovered.map((t) => (
                <Tag key={t} display={t} fill="outline" size="XS" style={{ fontSize: 10 }} />
              ))}
            </div>
          </div>
        )}

        {/* Pattern type pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Tag
            display={insight.patternType === "edge_behavior" ? "Edge Behavior" : "Evidence Synthesis"}
            fill="outline"
          />
          {insight.clusterDisplay && <Tag display={insight.clusterDisplay} fill="outline" />}
        </div>
      </div>
    );
  }

  // ── Tag-level / Sub-theme level view (existing) ────────────
  const lens = insight.lensUsed;
  const contrast = insight.contrast;
  const sliceCounts = insight.metrics?.sliceCounts;

  if (!lens) return <UI_CAPTION color={colors2.grey40}>No lens info available</UI_CAPTION>;

  const totalSliceCount = sliceCounts ? Object.values(sliceCounts).reduce((s, v) => s + v, 0) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Lens pills card */}
      <div style={{ background: colors2.grey2, borderRadius: 10, padding: "14px 16px" }}>
        <div style={{ display: "flex", gap: 24, marginBottom: 10 }}>
          <div>
            <UI_CAPTION style={{ color: colors2.grey40, fontSize: 11, marginBottom: 4 }}>Primary Lens</UI_CAPTION>
            <Tag display={lens.primary?.label || "N/A"} fill="outline" />
          </div>
          <div>
            <UI_CAPTION style={{ color: colors2.grey40, fontSize: 11, marginBottom: 4 }}>Secondary Lens</UI_CAPTION>
            <Tag display={lens.secondary?.label || "N/A"} fill="outline" />
          </div>
        </div>
        {lens.reason && (
          <UI_CAPTION style={{ color: colors2.grey50, fontSize: 11, lineHeight: 1.4 }}>
            {lens.reason}
          </UI_CAPTION>
        )}
      </div>

      {/* Contrast detection */}
      {contrast && (
        <div style={{ borderRadius: 10, padding: "12px 16px", background: "#EFF6FF", borderLeft: `4px solid ${colors2.blue}` }}>
          <UI_CAPTION style={{ fontWeight: 700, color: colors2.blue, marginBottom: 4 }}>
            Segment Contrast Detected
          </UI_CAPTION>
          <UI_CAPTION style={{ color: colors2.grey60, lineHeight: 1.4 }}>
            {contrast.sliceA} vs {contrast.sliceB} — {contrast.maxDiffPct} percentage point difference
          </UI_CAPTION>
        </div>
      )}

      {/* Slice breakdown with bars */}
      {sliceCounts && Object.keys(sliceCounts).length > 0 && (
        <div>
          <UI_BODY_SEMIBOLD style={{ marginBottom: 8, fontSize: 13 }}>Slice Breakdown</UI_BODY_SEMIBOLD>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.entries(sliceCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([sliceName, count]) => {
                const pct = totalSliceCount > 0 ? Math.round((count / totalSliceCount) * 100) : 0;
                return (
                  <div key={sliceName} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UI_CAPTION style={{ minWidth: 80, fontSize: 12 }}>{sliceName}</UI_CAPTION>
                    <div style={{ flex: 1, height: 10, background: colors2.grey5, borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: colors2.blue, borderRadius: 5, transition: "width 0.3s" }} />
                    </div>
                    <UI_CAPTION style={{ fontSize: 11, color: colors2.grey40, minWidth: 55, textAlign: "right" }}>
                      {count} ({pct}%)
                    </UI_CAPTION>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Pattern type + cluster as pills */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {insight.patternType && (
          <Tag
            display={insight.patternType === "segment_contrast" ? "Segment Contrast" : "General Pattern"}
            fill="outline"
          />
        )}
        {insight.clusterDisplay && (
          <Tag display={insight.clusterDisplay} fill="outline" />
        )}
      </div>
    </div>
  );
};

const TABS = [
  // { value: "findings", display: "Findings" }, // commented out — chunks now in Evidences
  { value: "evidences", display: "Evidences" },
  { value: "sources", display: "Sources" },
];

const TABS_V2 = [
  { value: "evidences", display: "Evidence" },
  // { value: "lens", display: "Lens" },
];

// ── Main lightbox ────────────────────────────────────────────────

export const InsightDetailLightbox = ({ insight, onClose }) => {
  if (!insight) return null;

  const [activeTab, setActiveTab] = useState("evidences");
  const compositeScore10 = insight.scores10?.composite ?? Math.round((insight.scores?.composite || 0) * 10);
  const compositeColor = getScoreColor(insight.scores?.composite || 0);

  // Detect V2 findings: they have evidence[] instead of samples[]
  const isV2 = Array.isArray(insight.evidence) && insight.evidence.length > 0 && insight.evidence[0]?.text;

  const isEvidenceSynthesis = insight.patternType === "evidence_synthesis" || insight.patternType === "edge_behavior" || !!insight.synthesisContext;

  // V1: Collect sample chunk IDs for the Evidences tab.
  // Samples can be a flat array (prevalence) or split object { tagA: [...], tagB: [...] } (pair insights).
  const samples = insight.samples || [];
  const isSplitSamples = !Array.isArray(samples) && samples.tagA;
  const sampleChunkIds = isSplitSamples
    ? [...(samples.tagA || []), ...(samples.tagB || [])].map((s) => s.chunkId).filter(Boolean)
    : (Array.isArray(samples) ? samples : []).map((s) => s.chunkId).filter(Boolean);

  const activeDimensions = isEvidenceSynthesis ? DIMENSIONS_EVIDENCE_SYNTHESIS : isV2 ? DIMENSIONS_V2 : DIMENSIONS;
  const activeTabs = isV2 ? TABS_V2 : TABS;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 480,
        height: "100vh",
        background: colors2.white,
        borderLeft: `1px solid ${colors2.grey10}`,
        boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "16px 20px",
          borderBottom: `1px solid ${colors2.grey10}`,
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <UI_TITLE style={{ lineHeight: 1.4, marginBottom: 4 }}>
            {insight.heading || insight.tags?.map((t) => t.display).join(", ")}
          </UI_TITLE>
          <UI_BODY_SM style={{ lineHeight: 1.4, color: colors2.grey60 }}>
            {insight.sentence}
          </UI_BODY_SM> 
        </div>
        <ButtonGhost
          icon={"X"}
          onClick={onClose}
        />
      </div>

      {/* Scrollable body */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Confidence bar */}
        {/* <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <UI_BODY_SEMIBOLD>Confidence</UI_BODY_SEMIBOLD>
            <UI_BODY_SEMIBOLD style={{ color: compositeColor }}>{compositeScore10}/10</UI_BODY_SEMIBOLD>
          </div>
          <ScoreBarChart
            data={[{ label: "Confidence", value: compositeScore10, color: compositeColor }]}
            domain={10}
            barHeight={12}
            showValue={false}
          />
          <UI_CAPTION color={colors2.grey40} style={{ marginTop: 6, lineHeight: 1.4 }}>
            A composite metric that represents how reliable and actionable an insight is,
            calculated by combining multiple evidence dimensions shown below.
          </UI_CAPTION>
        </div> */}

        {/* Underlying Pattern — the statistical sentence before LLM rewrite */}
        {insight.templateSentence && insight.templateSentence !== insight.sentence && !isV2 && (
          <div>
            <UI_CAPTION style={{ fontWeight: 600, marginBottom: 4 }}>Statistical Pattern</UI_CAPTION>
            <div style={{ background: colors2.grey2, borderRadius: 6, padding: "8px 12px" }}>
              <UI_CAPTION color={colors2.grey50} style={{ lineHeight: 1.4 }}>
                {insight.templateSentence}
              </UI_CAPTION>
            </div>
          </div>
        )}

        {/* Dimension rows — commented out per team feedback (confusing scoring UI)
        <div>
          {activeDimensions.map((dim) => {
            let detail = insight.details?.[dim.id] || {};
            if (isEvidenceSynthesis && !insight.details) {
              const ctx = insight.synthesisContext || {};
              const m = insight.metrics || {};
              if (dim.id === "sourceDiversity") detail = { documents: m.totalDocuments, total: m.datasetDocuments || ctx.documentsUsed || m.totalDocuments };
              else if (dim.id === "evidenceStrength") detail = { label: `${(ctx.evidenceStrengthLabel || "moderate").charAt(0).toUpperCase()}${(ctx.evidenceStrengthLabel || "moderate").slice(1)} — ${ctx.quotesUsed || 0} quotes from ${ctx.documentsUsed || 0} documents` };
              else if (dim.id === "quoteDensity") detail = { count: insight.evidence?.length || 0 };
            }
            return (
              <DimensionRow
                key={dim.id}
                dim={dim}
                score={insight.scores?.[dim.id] || 0}
                detail={detail}
              />
            );
          })}
        </div>
        */}

        {/* Slice × Attribute breakdown chart */}
        {/* {isV2 && (() => {
          const chartData = buildSliceChartData(insight);
          if (!chartData) return null;

          const primaryLabel = insight.lensUsed?.primary?.label || "Segments";
          const secondaryKey = insight.lensUsed?.secondary?.key;
          const secondaryLabel = ATTRIBUTE_LABELS[secondaryKey] || insight.lensUsed?.secondary?.label || "Attribute";

          if (!chartData.isBreakdown) {
            // Single slice — simple bar chart of attribute distribution
            return (
              <div>
                <UI_BODY_SEMIBOLD style={{ fontSize: 13, marginBottom: 8 }}>
                  {secondaryLabel} Distribution
                </UI_BODY_SEMIBOLD>
                <BarChart
                  data={chartData.data}
                  valuePath="count"
                  labelPath="display"
                  showCount
                  showPercent
                />
              </div>
            );
          }

          // Breakdown: grouped bars — slices × attribute values
          const maxChildren = Math.max(...chartData.data.map(d => d.children?.length || 0));
          const valuePath = Array.from({ length: maxChildren }, (_, i) => `children.${i}.count`);
          const tooltipLabelsPath = Array.from({ length: maxChildren }, (_, i) => `children.${i}.display`);

          return (
            <div>
              <UI_BODY_SEMIBOLD style={{ fontSize: 13, marginBottom: 8 }}>
                {primaryLabel} × {secondaryLabel}
              </UI_BODY_SEMIBOLD>
              <BarChart
                data={chartData.data}
                valuePath={valuePath}
                tooltipLabelsPath={tooltipLabelsPath}
                labelPath="display"
                breakdownDisplayType="grouped"
                breakdownCategoryName={secondaryLabel}
                showCount
                showPercent
              />
            </div>
          );
        })()} */}

        {/* Tabbed content */}
        <div>
          {activeTabs.length > 1 ?
            <TabBarV2
              options={activeTabs}
              value={activeTab}
              onChange={(id, v) => setActiveTab(v)}
              saveValueAsString
            /> 
            :
            <UI_BODY_BOLD>{insight.evidence.length > 1 ? `Evidences (${insight.evidence.length})` : `Evidence (${insight.evidence.length})`}</UI_BODY_BOLD>
          }
          <div style={{ marginTop: 12 }}>
            {activeTab === "evidences" && isV2 && <V2EvidencesTab evidence={insight.evidence} contentTypes={insight.contentTypes} />}
            {activeTab === "evidences" && !isV2 && <EvidencesTab chunkIds={sampleChunkIds} />}
            {activeTab === "lens" && <LensTab insight={insight} />}
            {activeTab === "sources" && <SourcesTab contentTypes={insight.contentTypes} />}
          </div>
        </div>
      </div>
    </div>
  );
};
